import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, from, of } from 'rxjs';
import { catchError, tap, switchMap, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  License,
  Category,
  Question,
  Progress,
  CategoryProgress,
  SubmitAnswerRequest,
  SubmitAnswerResponse,
  ExamBogen,
  ExamBogenDetails,
  QuestionProgress,
  ExtendedStatistics,
  ExamResultsTableData,
  GuideArticle,
  GuideAdminArticle
} from '../models/models';
import { NetworkStateService } from '../shared/services/network-state.service';
import { OfflineDbService } from '../shared/services/offline-db.service';
import { CapacitorService } from '../shared/services/capacitor.service';
import { UserSettings } from '../shared/models/models';

// ...existing code...
export interface RegistrationErrorLogPayload {
  email?: string;
  status?: number;
  message?: string;
  backendMessage?: string;
  details?: any;
  source?: string;
  url?: string;
  contentType?: string;
}

/**
 * API Service mit Offline-Support
 *
 * Features:
 * - Automatisches Caching von Fragen in SQLite
 * - Offline-Fallback für alle wichtigen Methoden
 * - Netzwerk-Status-Erkennung
 */
@Injectable({
  providedIn: 'root'
})
export class ApiService {
    /**
     * Holt das aktuelle User-Profil vom Backend
     */
    getProfile(): Observable<any> {
      return this.http.get<any>(`${this.apiUrl}/users/profile.php`);
    }
    /**
     * Holt User-Preferences (preferred_scheine)
     */
    getUserPreferences(): Observable<{ preferred_scheine: number[] | null }> {
      return this.http.get<{ preferred_scheine: number[] | null }>(`${this.apiUrl}/users/preferences.php`).pipe(
        map((res) => ({
          preferred_scheine: Array.isArray(res?.preferred_scheine) ? res.preferred_scheine : null
        }))
      );
    }
    /**
     * Speichert User-Preferences (preferred_scheine)
     */
    saveUserPreferences(preferred: number[] | null): Observable<{ preferred_scheine: number[] | null }> {
      return this.http.put<{ preferred_scheine: number[] | null }>(`${this.apiUrl}/users/preferences.php`, {
        preferred_scheine: preferred
      }).pipe(
        map((res) => ({
          preferred_scheine: Array.isArray(res?.preferred_scheine) ? res.preferred_scheine : null
        }))
      );
    }
    // ===== GUIDES =====

    getGuideArticles(): Observable<GuideArticle[]> {
      if (this.networkState.isOffline()) {
        console.warn('[API] Browser offline - Guides nicht verfügbar');
        return of([]);
      }

      return this.http.get<{ items: GuideArticle[] }>(`${this.apiUrl}/guides/`).pipe(
        map(response => response?.items ?? []),
        catchError(error => {
          console.error('[API] Fehler beim Laden der Guides:', error);
          return of([]);
        })
      );
    }

    getGuideAdminArticles(): Observable<GuideAdminArticle[]> {
      return this.http.get<{ items: GuideAdminArticle[] }>(`${this.apiUrl}/admin/guides.php`).pipe(
        map(response => response?.items ?? []),
        catchError(error => {
          console.error('[API] Admin-Guides konnten nicht geladen werden:', error);
          return of([]);
        })
      );
    }

    saveGuideArticle(formData: FormData): Observable<any> {
      return this.http.post<any>(`${this.apiUrl}/admin/guides.php`, formData).pipe(
        catchError(error => {
          console.error('[API] Fehler beim Speichern des Guides:', error);
          throw error;
        })
      );
    }
    getGuideBySlug(slug: string): Observable<GuideArticle | null> {
      const params = { slug };
      return this.http.get<{ article: GuideArticle }>(`${this.apiUrl}/guides/slug.php`, { params }).pipe(
        map(res => res?.article ?? null),
        catchError(err => {
          console.error('[API] Fehler beim Laden des Guides per Slug:', err);
          return of(null);
        })
      );
    }
  
  private apiUrl = environment.apiUrl;
  private isNativeApp = false; // Wird true wenn Capacitor erkannt wird
  public get registerEndpoint(): string {
    return `${this.apiUrl}/auth/register/`;
  }

  constructor(
    private http: HttpClient,
    private networkState: NetworkStateService,
    private offlineDb: OfflineDbService,
    private capacitorService: CapacitorService
  ) {
    // Nutze die zentrale Methode für die Erkennung
    this.isNativeApp = this.capacitorService.isNativeApp();
    console.log('[API] Service initialisiert - Native App:', this.isNativeApp);
  }

  // ===== LICENSES =====

  getLicenses(): Observable<License[]> {
    // Wenn offline, versuche aus DB zu laden
    if (this.isNativeApp && this.networkState.isOffline()) {
      console.warn('[API] Offline: Lade Scheine aus lokaler DB');
      return from(this.offlineDb.dbReadyPromise).pipe(
        switchMap(() => from(this.offlineDb.getLicenses())),
        tap(licenses => {
          if (licenses.length === 0) {
            console.warn('[API] Keine Scheine in lokaler DB');
          } else {
            console.log('[API] Scheine aus DB geladen:', licenses.length);
          }
        }),
        catchError(error => {
          console.error('[API] Fehler beim Laden der Scheine aus DB:', error);
          return of([]);
        })
      );
    }

    // Online: Lade vom Server und speichere in DB
    return this.http.get<any>(`${this.apiUrl}/licenses/`).pipe(
      map((res: any) => {
        const items: License[] = Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res)
            ? res
            : res?.lizenzen || [];
        return items;
      }),
      tap(licenses => {
        console.log('[API] Scheine vom Server geladen:', licenses.length);
        if (this.isNativeApp && licenses.length > 0) {
          console.log(`[API] Speichere ${licenses.length} Scheine in lokaler DB`);
          this.offlineDb.dbReadyPromise
            .then(() => this.offlineDb.upsertLicenses(licenses))
            .catch((err: any) => {
              console.error('[API] Fehler beim Speichern der Scheine:', err);
            });
        }
      }),
      catchError(error => {
        console.error('[API] Fehler beim Laden der Scheine vom Server:', error);
        if (error && typeof error === 'object') {
          console.error('[API] Error-Details:', {
            message: error.message,
            status: error.status,
            errorObj: error.error,
            url: error.url,
            name: error.name,
            stack: error.stack
          });
        }
        if (this.isNativeApp) {
          console.warn('[API] Fallback zu lokaler DB');
          return from(this.offlineDb.getLicenses());
        }
        return of([]);
      })
    );
  }

    // ===== CATEGORIES =====

  getCategories(schein: string): Observable<Category[]> {
    console.log(`[API] getCategories aufgerufen - schein: ${schein}, offline: ${this.networkState.isOffline()}`);

    if (this.isNativeApp) {
      return from(this.offlineDb.dbReadyPromise).pipe(
        switchMap(() => this.offlineDb.getCategories(schein)),
        switchMap(categories => {
          if (categories && categories.length > 0) {
            return of(categories);
          }
          return from(this.offlineDb.getQuestions(schein)).pipe(
            map(questions => this.buildCategoriesFromQuestions(questions, schein)),
            tap(derived => {
              if (derived?.length) {
                // Speichere abgeleitete Kategorien für den nächsten Start
                this.offlineDb.upsertCategories(schein, derived).catch((err: any) =>
                  console.error('[API] Fehler beim Speichern abgeleiteter Kategorien:', err)
                );
              }
            }),
            catchError(() => of([]))
          );
        }),
        switchMap(categories => {
          if ((categories && categories.length > 0) || this.networkState.isOffline()) {
            return of(categories);
          }

          const params = new HttpParams().set('schein', schein);
          return this.http.get<Category[]>(`${this.apiUrl}/categories/categories.php`, { params }).pipe(
            tap(serverCats => {
              if (serverCats?.length) {
                (async () => {
                  await this.offlineDb.dbReadyPromise;
                  await this.offlineDb.upsertCategories(schein, serverCats);
                })().catch((err: any) => console.error('[API] Fehler beim Speichern in DB:', err));
              }
            }),
            catchError(error => {
              console.error('[API] Fehler beim Laden vom Server:', error);
              return of(categories || []);
            })
          );
        }),
        catchError(err => {
          console.error('[API] Fehler getCategories offline-first:', err);
          if (this.networkState.isOffline()) {
            return of([]);
          }
          const params = new HttpParams().set('schein', schein);
          return this.http.get<Category[]>(`${this.apiUrl}/categories/categories.php`, { params });
        })
      );
    }

    if (this.networkState.isOffline()) {
      console.warn('[API] Browser offline - keine Kategorien verfügbar');
      return of([]);
    }

    const params = new HttpParams().set('schein', schein);
    return this.http.get<Category[]>(`${this.apiUrl}/categories/categories.php`, { params });
  }

// ===== QUESTIONS =====

  getQuestions(schein?: string, kategorie?: string, unterkategorie?: string): Observable<Question[]> {
    console.log(`[API] getQuestions aufgerufen - schein: ${schein}, kategorie: ${kategorie}, offline: ${this.networkState.isOffline()}`);

    if (this.isNativeApp && schein) {
      return from(this.offlineDb.getQuestions(schein, kategorie, unterkategorie)).pipe(
        switchMap(localQs => {
          if (localQs && localQs.length > 0) {
            return of(localQs);
          }

          if (this.networkState.isOffline()) {
            return of([]);
          }

          let params = new HttpParams();
          if (schein) params = params.set('schein', schein);
          if (kategorie) params = params.set('kategorie', kategorie);
          if (unterkategorie) params = params.set('unterkategorie', unterkategorie);

          return this.http.get<Question[]>(`${this.apiUrl}/questions/`, { params }).pipe(
            tap(questions => {
              questions.forEach(q => {
                if (!q.schein && (q as any).schein_kurz) {
                  q.schein = (q as any).schein_kurz;
                }
              });

              if (questions.length > 0) {
                this.offlineDb.upsertQuestions(questions).catch((err: any) => {
                  console.error('[API] Fehler beim Speichern in DB:', err);
                });
              }
            }),
            catchError(error => {
              console.error('[API] Fehler beim Laden vom Server:', error);
              return of(localQs || []);
            })
          );
        }),
        catchError(err => {
          console.error('[API] Fehler getQuestions offline-first:', err);
          if (this.networkState.isOffline()) {
            return of([]);
          }

          let params = new HttpParams();
          if (schein) params = params.set('schein', schein);
          if (kategorie) params = params.set('kategorie', kategorie);
          if (unterkategorie) params = params.set('unterkategorie', unterkategorie);

          return this.http.get<Question[]>(`${this.apiUrl}/questions/`, { params }).pipe(
            tap(questions => {
              questions.forEach(q => {
                if (!q.schein && (q as any).schein_kurz) {
                  q.schein = (q as any).schein_kurz;
                }
              });
            })
          );
        })
      );
    }

    if (this.networkState.isOffline()) {
      console.warn('[API] Browser offline - keine Fragen verfügbar');
      return of([]);
    }

    let params = new HttpParams();
    if (schein) params = params.set('schein', schein);
    if (kategorie) params = params.set('kategorie', kategorie);
    if (unterkategorie) params = params.set('unterkategorie', unterkategorie);

    return this.http.get<Question[]>(`${this.apiUrl}/questions/`, { params }).pipe(
      tap(questions => {
        questions.forEach(q => {
          if (!q.schein && (q as any).schein_kurz) {
            q.schein = (q as any).schein_kurz;
          }
        });
      })
    );
  }
  private buildCategoriesFromQuestions(questions: Question[], schein: string): Category[] {
    const mapCat = new Map<string, any>();
    (questions || []).forEach(q => {
      if (!q.kategorie) {
        return;
      }
      const key = `${q.kategorie}|${q.unterkategorie || ''}`;
      if (!mapCat.has(key)) {
        mapCat.set(key, {
          id: 0,
          schein,
          kategorie: q.kategorie,
          unterkategorie: q.unterkategorie || '',
          name: q.kategorie,
          name_unterkategorie: q.unterkategorie || '',
          beschreibung: '',
          anzahl_fragen: 0
        } as any);
      }
      const entry = mapCat.get(key) as any;
      entry.anzahl_fragen = (entry.anzahl_fragen || 0) + 1;
    });
    return Array.from(mapCat.values());
  }


  getQuestion(frageId: number): Observable<any> {
    // Offline: Aus DB laden
    if (this.isNativeApp && this.networkState.isOffline()) {
      console.log('[API] Offline: Lade Frage aus DB');
      return from(this.offlineDb.getQuestion(frageId)).pipe(
        tap(question => {
          if (question) {
            console.log('[API] Frage aus DB geladen');
          } else {
            console.warn('[API] Frage nicht in DB gefunden');
          }
        }),
        catchError(() => of(null))
      );
    }

    // Online: Vom Server laden
    return this.http.get<Question>(`${this.apiUrl}/questions/${frageId}/`).pipe(
      catchError(error => {
        // Fallback zur DB
        if (this.isNativeApp) {
          console.warn('[API] API-Fehler, versuche DB-Fallback');
          return from(this.offlineDb.getQuestion(frageId));
        }
        throw error;
      })
    );
  }

  getQuestionImageUrl(frageId: number): string {
    // Backend liefert Bilder unter image.php?id=...
    return `${this.apiUrl}/questions/image.php?id=${frageId}`;
  }

  getAnswerImageUrl(frageId: number, buchstabe: string): string {
    return `${this.apiUrl}/questions/answer-image.php?id=${frageId}&buchstabe=${buchstabe}`;
  }

  private async downloadAndStoreQuestionImages(questions: Question[]): Promise<void> {
    if (!this.isNativeApp) return;
    await this.offlineDb.dbReadyPromise;
    for (const q of questions) {
      // Fragebild
      if (q.has_image) {
        const imgUrl = this.getQuestionImageUrl(q.frage_id);
        const dataUrl = await this.fetchImageAsDataUrl(imgUrl);
        if (dataUrl) {
          await this.offlineDb.setQuestionImage(q.frage_id, dataUrl);
        }
      }

      // Antwortbilder
      if (q.antworten && q.antworten.length > 0) {
        for (const ans of q.antworten as any[]) {
          if ((ans as any).has_image) {
            const inlineData = ans?.image_data || ans?.image || ans?.image_base64 || ans?.bild_base64 || ans?.base64;
            if (inlineData) {
              const dataUrl = inlineData.startsWith('data:') ? inlineData : `data:image/png;base64,${inlineData}`;
              await this.offlineDb.setAnswerImage(q.frage_id, ans.buchstabe, dataUrl);
              continue;
            }
            const url = this.getAnswerImageUrl(q.frage_id, ans.buchstabe);
            const dataUrl = await this.fetchImageAsDataUrl(url);
            if (dataUrl) {
              await this.offlineDb.setAnswerImage(q.frage_id, ans.buchstabe, dataUrl);
            }
          }
        }
      }
    }
  }

  private async fetchImageAsDataUrl(url: string): Promise<string | null> {
    try {
      let token = localStorage.getItem('token');
      if (!token) {
        try {
          token = await this.offlineDb.getToken();
        } catch (e) {
          console.warn('[API] Konnte Token nicht aus SQLite laden', e);
        }
      }
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const resp = await fetch(url, { headers });
      if (!resp.ok) return null;
      const blob = await resp.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      console.warn('[API] Bild-Download fehlgeschlagen:', e);
      return null;
    }
  }

  // ===== OFFLINE SCHEIN DOWNLOAD =====

  /**
   * Lädt alle Fragen und Kategorien eines Scheins für Offline-Nutzung herunter
   * @param schein - Der zu herunterladende Schein (z.B. "SBF-See")
   * @returns Observable mit Progress-Updates und Abschluss-Status
   */
  downloadCompleteSchein(schein: string, scheinId?: number | null): Observable<{progress: number, status: string, complete: boolean}> {
    if (!this.isNativeApp) {
      console.error('[API] downloadCompleteSchein nur in nativer App verfügbar');
      return of({progress: 0, status: 'Nur in App verfügbar', complete: false});
    }

    if (this.networkState.isOffline()) {
      console.error('[API] downloadCompleteSchein: Offline - Download nicht möglich');
      return of({progress: 0, status: 'Offline - Bitte verbinde dich mit dem Internet', complete: false});
    }

    return new Observable(observer => {
      console.log(`[API] Starte Download von Schein: ${schein}`);

      // Schritt 1: Lade alle Kategorien
      observer.next({progress: 10, status: 'Lade Kategorien...', complete: false});

      this.getCategories(schein).subscribe({
        next: async (categories) => {
          console.log(`[API] ${categories.length} Kategorien geladen`);
          observer.next({progress: 25, status: `${categories.length} Kategorien geladen`, complete: false});

          // Explizit Kategorien in DB speichern
          try {
            await this.offlineDb.dbReadyPromise;
            await this.offlineDb.upsertCategories(schein, categories);
            console.log('[API] Kategorien in DB gespeichert');
            observer.next({progress: 30, status: 'Kategorien gespeichert', complete: false});
          } catch (err) {
            console.error('[API] Fehler beim Speichern der Kategorien:', err);
            // Fortfahren trotz Fehler
          }

          // Schritt 2: Lade ALLE Fragen für diesen Schein (ohne Kategoriefilter)
          observer.next({progress: 40, status: 'Lade alle Fragen...', complete: false});

          let params = new HttpParams().set('schein', schein);
          this.http.get<Question[]>(`${this.apiUrl}/questions/`, { params }).subscribe({
            next: (questions) => {
              console.log(`[API] ${questions.length} Fragen vom Server geladen`);

              // FIX: Backend gibt schein_kurz zurück, aber wir brauchen schein
              // Mappe schein_kurz -> schein für alle Fragen
              questions.forEach(q => {
                if (!q.schein && (q as any).schein_kurz) {
                  q.schein = (q as any).schein_kurz;
                }
              });

              console.log(`[API] Fragen gemappt - schein gesetzt aus schein_kurz`);

              observer.next({progress: 70, status: `${questions.length} Fragen geladen`, complete: false});

              // Schritt 3: Speichere in DB
              observer.next({progress: 75, status: 'Speichere in Datenbank...', complete: false});

              this.offlineDb.upsertQuestions(questions).then(async () => {
                console.log('[API] Fragen gespeichert');
                observer.next({progress: 80, status: 'Bilder werden gespeichert...', complete: false});

                // Lade alle Frage- und Antwortbilder und speichere sie offline
                try {
                  await this.downloadAndStoreQuestionImages(questions);
                } catch (imgErr) {
                  console.warn('[API] Fehler beim Bild-Download (weiter mit Markierung):', imgErr);
                }

                observer.next({progress: 90, status: 'Markiere als heruntergeladen...', complete: false});

                // Schritt 4: Markiere Schein als heruntergeladen
                try {
                  await this.offlineDb.dbReadyPromise;
                  let resolvedId = scheinId;
                  if (resolvedId === undefined) {
                    const licenses = await this.offlineDb.getLicenses();
                    const match = licenses.find((l: any) =>
                      l.schein === schein || l.schein_kurz === schein || l.name === schein || l.schein_lang === schein
                    );
                    resolvedId = match?.id ?? null;
                  }
                  await this.offlineDb.markScheinAsDownloaded(schein, questions.length, resolvedId ?? null);
                  console.log(`[API] Schein ${schein} erfolgreich heruntergeladen`);
                  observer.next({
                    progress: 100,
                    status: `${schein} erfolgreich heruntergeladen (${questions.length} Fragen)`,
                    complete: true
                  });
                  observer.complete();
                } catch (err) {
                  console.error('[API] Fehler beim Markieren:', err);
                  observer.error({progress: 90, status: 'Fehler beim Speichern', complete: false});
                }
              }).catch((err: any) => {
                console.error('[API] Fehler beim Speichern der Fragen:', err);
                observer.error({progress: 75, status: 'Fehler beim Speichern', complete: false});
              });
            },
            error: (err) => {
              console.error('[API] Fehler beim Laden der Fragen:', err);
              observer.error({progress: 40, status: 'Fehler beim Laden der Fragen', complete: false});
            }
          });
        },
        error: (err) => {
          console.error('[API] Fehler beim Laden der Kategorien:', err);
          observer.error({progress: 10, status: 'Fehler beim Laden der Kategorien', complete: false});
        }
      });
    });
  }

  /**
   * Gibt alle offline verfügbaren Scheine zurück
   */
  getOfflineScheine(): Observable<string[]> {
    if (!this.isNativeApp) {
      return of([]);
    }
    return from(this.offlineDb.getOfflineScheine());
  }

  /**
   * Offline-Scheine mit IDs (nur native)
   */
  getOfflineScheineWithIds(): Observable<{ schein: string; schein_id: number | null }[]> {
    if (!this.isNativeApp) {
      return of([]);
    }
    return from(this.offlineDb.getOfflineScheineWithIds());
  }

  /**
   * Prüft ob ein Schein offline verfügbar ist
   */
  isScheinAvailableOffline(schein: string): Observable<boolean> {
    if (!this.isNativeApp) {
      return of(false);
    }
    return from(this.offlineDb.isScheinAvailableOffline(schein));
  }

  /**
   * Entfernt einen Schein aus dem Offline-Speicher
   */
  removeOfflineSchein(schein: string): Observable<void> {
    if (!this.isNativeApp) {
      return of(undefined);
    }

    return from(
      this.offlineDb.removeOfflineSchein(schein).then(() => {
        console.log(`[API] Offline-Markierung für ${schein} entfernt`);
      })
    );
  }

  /**
   * Prüft ob Updates für einen Schein verfügbar sind
   * Vergleicht die Anzahl der Fragen auf dem Server mit der lokalen DB
   */
  checkScheinForUpdates(schein: string): Observable<{hasUpdate: boolean, serverCount: number, localCount: number}> {
    if (!this.isNativeApp) {
      return of({hasUpdate: false, serverCount: 0, localCount: 0});
    }

    if (this.networkState.isOffline()) {
      return of({hasUpdate: false, serverCount: 0, localCount: 0});
    }

    return new Observable(observer => {
      // 1. Hole lokale Fragenanzahl
      this.offlineDb.getScheinDownloadInfo(schein).then(localInfo => {
        const localCount = localInfo?.questionCount || 0;

        // 2. Hole Server-Fragenanzahl (nur die Anzahl, nicht die Fragen selbst)
        let params = new HttpParams().set('schein', schein);
        this.http.get<Question[]>(`${this.apiUrl}/questions/`, { params }).subscribe({
          next: (questions) => {
            const serverCount = questions.length;
            const hasUpdate = serverCount !== localCount;

            console.log(`[API] Update-Check für ${schein}: Server=${serverCount}, Lokal=${localCount}, Update=${hasUpdate}`);

            observer.next({hasUpdate, serverCount, localCount});
            observer.complete();
          },
          error: (err) => {
            console.error('[API] Fehler beim Update-Check:', err);
            observer.next({hasUpdate: false, serverCount: 0, localCount});
            observer.complete();
          }
        });
      }).catch((err: any) => {
        console.error('[API] Fehler beim Laden der lokalen Infos:', err);
        observer.next({hasUpdate: false, serverCount: 0, localCount: 0});
        observer.complete();
      });
    });
  }

  /**
   * Synchronisiert einen offline verfügbaren Schein mit dem Server
   * Lädt neue/geänderte Fragen herunter
   */
  syncSchein(schein: string): Observable<{progress: number, status: string, complete: boolean}> {
    // Nutzt die gleiche Logik wie downloadCompleteSchein
    return this.downloadCompleteSchein(schein);
  }

  // ===== EXAM CONTENT DOWNLOAD =====

  /**
   * Lädt alle Exam-Bögen mit Fragen für einen Schein herunter
   */
  downloadCompleteScheinExams(schein: string, scheinId?: number | null): Observable<{progress: number, status: string, complete: boolean}> {
    if (!this.isNativeApp) {
      console.error('[API] downloadCompleteScheinExams nur in nativer App verfügbar');
      return of({progress: 0, status: 'Nur in App verfügbar', complete: false});
    }

    if (this.networkState.isOffline()) {
      console.error('[API] downloadCompleteScheinExams: Offline - Download nicht möglich');
      return of({progress: 0, status: 'Offline - Bitte verbinde dich mit dem Internet', complete: false});
    }

    return new Observable(observer => {
      console.log(`[API] Starte Download von Exam-Bögen für Schein: ${schein}`);

      // Schritt 1: Lade alle Exam-Bögen
      observer.next({progress: 10, status: 'Lade Exam-Bögen...', complete: false});

      this.getExamBoegen().subscribe({
        next: (response) => {
          let boegen: any[] = [];

          if (Array.isArray(response)) {
            boegen = response;
          } else if (response && response.boegen && Array.isArray(response.boegen)) {
            boegen = response.boegen;
          }

          // Filtere Bögen für den gewählten Schein (Kurz- oder Langform zulassen)
          const scheineBoegen = boegen.filter((b: any) => {
            const kurz = b.schein_kurz || b.schein || '';
            const lang = b.schein_lang || b.schein || '';
            return (
              kurz === schein ||
              lang === schein ||
              (typeof lang === 'string' && lang.includes(schein)) ||
              (typeof kurz === 'string' && kurz.includes(schein))
            );
          });
          console.log('[ExamDbg][DownloadExams] boegen total=', boegen.length, 'filtered=', scheineBoegen.length, 'schein=', schein, 'sample=', scheineBoegen[0]);

          if (scheineBoegen.length === 0) {
            observer.next({progress: 0, status: `Keine Bögen für ${schein} gefunden`, complete: false});
            observer.complete();
            return;
          }

          console.log(`[API] ${scheineBoegen.length} Bögen für ${schein} gefunden`);
          observer.next({progress: 30, status: `${scheineBoegen.length} Bögen gefunden`, complete: false});

          // Schritt 2: Lade Details für jeden Bogen (mit Fragen)
          const totalBoegen = scheineBoegen.length;
          let loadedBoegen = 0;

          const loadBogenDetails = async (index: number) => {
            if (index >= totalBoegen) {
              // Alle Bögen geladen
                let resolvedId = scheinId;
                if (resolvedId === undefined) {
                  await this.offlineDb.dbReadyPromise;
                  const licenses = await this.offlineDb.getLicenses();
                  const match = licenses.find((l: any) =>
                    l.schein === schein || l.schein_kurz === schein || l.name === schein || l.schein_lang === schein
                  );
                  resolvedId = match?.id ?? null;
                }
                this.offlineDb.markScheinExamsAsDownloaded(schein, totalBoegen, resolvedId ?? null).then(() => {
                console.log(`[API] Exam-Bögen für ${schein} erfolgreich heruntergeladen`);
                observer.next({
                  progress: 100,
                  status: `Alle ${totalBoegen} Bögen erfolgreich heruntergeladen`,
                  complete: true
                });
                observer.complete();
              }).catch((err: any) => {
                console.error('[API] Fehler beim Markieren:', err);
                observer.error({progress: 90, status: 'Fehler beim Speichern', complete: false});
              });
              return;
            }

            const bogen = scheineBoegen[index];
            const progress = 30 + ((loadedBoegen / totalBoegen) * 60); // 30% -> 90%
            // Halte vorhandene Schein-Felder aus der Liste fest, falls Detail-Response sie nicht liefert
            const listBogen = scheineBoegen[index] || {};
            const fallbackKurz = listBogen.schein_kurz || listBogen.schein || schein;
            const fallbackLang = listBogen.schein_lang || listBogen.schein || fallbackKurz || schein;

            observer.next({
              progress: Math.round(progress),
              status: `Lade Bogen ${loadedBoegen + 1}/${totalBoegen}...`,
              complete: false
            });

            // Lade Bogen-Details mit Fragen
            this.http.get<any>(`${this.apiUrl}/exams/boegen.php?id=${bogen.id}`).subscribe({
              next: (details) => {
                if (details && details.bogen && details.fragen) {
                  const scheinKurz = details.bogen.schein_kurz || details.bogen.schein || schein;
                  const bogenToSave = {
                    id: details.bogen.id,
                    name: details.bogen.name,
                    beschreibung: details.bogen.beschreibung,
                    schein: details.bogen.schein || scheinKurz || fallbackKurz || schein,
                    schein_kurz: scheinKurz || fallbackKurz,
                    schein_lang: details.bogen.schein_lang || fallbackLang,
                    kurs_id: details.bogen.kurs_id,
                    zeitlimit_minuten: details.bogen.zeitlimit_minuten,
                    bestehensgrenze_prozent: details.bogen.bestehensgrenze_prozent,
                    anzahl_fragen: details.anzahl_fragen,
                    is_offiziel: details.bogen.is_offiziel || 0,
                    fragen: details.fragen
                  };

                  const saveImages = async () => {
                    for (const q of details.fragen) {
                      const qId = (q as any).frage_id || (q as any).id;
                      if (q.has_image && qId) {
                        const imgUrl = this.getQuestionImageUrl(qId);
                        const dataUrl = await this.fetchImageAsDataUrl(imgUrl);
                        if (dataUrl) {
                          await this.offlineDb.setQuestionImage(qId, dataUrl);
                        }
                      }
                      if (q.antworten && Array.isArray(q.antworten)) {
                        for (const ans of q.antworten as any[]) {
                          if (ans.has_image && ans.buchstabe) {
                            const aUrl = this.getAnswerImageUrl(qId, ans.buchstabe);
                            const dataUrl = await this.fetchImageAsDataUrl(aUrl);
                            if (dataUrl) {
                              await this.offlineDb.setAnswerImage(qId, ans.buchstabe, dataUrl);
                            }
                          }
                        }
                      }
                    }
                  };

                  this.offlineDb.upsertExamBoegen([bogenToSave]).then(() => {
                    saveImages().catch(err => console.warn('[API] Bild-Speichern für Exam-Bögen fehlgeschlagen', err));
                    loadedBoegen++;
                    loadBogenDetails(index + 1);
                  }).catch((err: any) => {
                    console.error('[API] Fehler beim Speichern von Bogen:', err);
                    loadedBoegen++;
                    loadBogenDetails(index + 1);
                  });
                } else {
                  loadedBoegen++;
                  loadBogenDetails(index + 1);
                }
              },
              error: (err) => {
                console.error(`[API] Fehler beim Laden von Bogen ${bogen.id}:`, err);
                loadedBoegen++;
                loadBogenDetails(index + 1);
              }
            });
          };

          loadBogenDetails(0);
        },
        error: (err) => {
          console.error('[API] Fehler beim Laden der Exam-Bögen:', err);
          observer.error({progress: 10, status: 'Fehler beim Laden der Bögen', complete: false});
        }
      });
    });
  }

  /**
   * Gibt alle Scheine mit offline verfügbaren Exam-Bögen zurück
   */
  getOfflineScheineExams(): Observable<string[]> {
    if (!this.isNativeApp) {
      return of([]);
    }
    return from(this.offlineDb.getOfflineScheineExams());
  }

  /**
   * Prüft ob Exam-Bögen für einen Schein offline verfügbar sind
   */
  isScheinExamsAvailableOffline(schein: string): Observable<boolean> {
    if (!this.isNativeApp) {
      return of(false);
    }
    return from(this.offlineDb.isScheinExamsAvailableOffline(schein));
  }

  /**
   * Prüft ob Updates für Exam-Bögen eines Scheins verfügbar sind
   */
  checkScheinExamsForUpdates(schein: string): Observable<{hasUpdate: boolean, serverCount: number, localCount: number}> {
    if (!this.isNativeApp) {
      return of({hasUpdate: false, serverCount: 0, localCount: 0});
    }

    if (this.networkState.isOffline()) {
      return of({hasUpdate: false, serverCount: 0, localCount: 0});
    }

    return new Observable(observer => {
      // 1. Hole lokale Bogen-Anzahl
      this.offlineDb.getScheinExamDownloadInfo(schein).then(localInfo => {
        const localCount = localInfo?.bogenCount || 0;

        // 2. Hole Server-Bogen-Anzahl
        this.http.get<any>(`${this.apiUrl}/exams/boegen.php`).subscribe({
          next: (response) => {
            let boegen: any[] = [];

            if (Array.isArray(response)) {
              boegen = response;
            } else if (response && response.boegen && Array.isArray(response.boegen)) {
              boegen = response.boegen;
            }

            const scheineBoegen = boegen.filter((b: any) => {
              return b.schein === schein || (b.name && b.name.includes(schein));
            });

            const serverCount = scheineBoegen.length;
            const hasUpdate = serverCount !== localCount;

            console.log(`[API] Exam-Update-Check für ${schein}: Server=${serverCount}, Lokal=${localCount}, Update=${hasUpdate}`);

            observer.next({hasUpdate, serverCount, localCount});
            observer.complete();
          },
          error: (err) => {
            console.error('[API] Fehler beim Exam-Update-Check:', err);
            observer.next({hasUpdate: false, serverCount: 0, localCount});
            observer.complete();
          }
        });
      }).catch((err: any) => {
        console.error('[API] Fehler beim Laden der lokalen Exam-Infos:', err);
        observer.next({hasUpdate: false, serverCount: 0, localCount: 0});
        observer.complete();
      });
    });
  }

  /**
   * Synchronisiert Exam-Bögen eines Scheins mit dem Server
   */
  syncScheinExams(schein: string): Observable<{progress: number, status: string, complete: boolean}> {
    // Nutzt die gleiche Logik wie downloadCompleteScheinExams
    return this.downloadCompleteScheinExams(schein);
  }

  // ===== PROGRESS & ANSWERS =====

  submitAnswer(data: SubmitAnswerRequest): Observable<any> {
    // Offline: In DB speichern für späteren Sync
    if (this.isNativeApp && this.networkState.isOffline()) {
      console.log('[API] Offline: Speichere Antwort lokal');
      return from(
        this.offlineDb.saveProgress({
          frage_id: data.frage_id,
          user_id: 0,
          antwort: data.selected_answer,
          ist_richtig: 0,
          zeit_sekunden: data.time_taken_seconds,
          updated_at: new Date().toISOString()
        })
      ).pipe(
        catchError(() => of({
          success: false,
          is_correct: false,
          korrekte_antwort: '',
          message: 'Offline - wird synchronisiert'
        }))
      );
    }

    // Online: An Server senden
    return this.http.post<SubmitAnswerResponse>(`${this.apiUrl}/progress/submit/`, data);
  }

  // ===== SESSIONS =====

  createSession(payload?: any) {
    const sanitizedPayload = payload && typeof payload === 'object' ? { ...payload } : {};
    delete sanitizedPayload.user_id;
    delete sanitizedPayload.userId;
    const body = Object.assign({ action: 'start' }, sanitizedPayload);

    if (this.isNativeApp && this.networkState.isOffline()) {
      return of({ session_id: 'offline-' + Date.now() });
    }

    return this.http.post<any>(`${this.apiUrl}/progress/session/`, body);
  }

  endSession(sessionId: number | string | null, durationSeconds: number, payload?: any) {
    const sanitizedPayload = payload && typeof payload === 'object' ? { ...payload } : {};
    delete sanitizedPayload.user_id;
    delete sanitizedPayload.userId;
    const body = Object.assign({ action: 'end', session_id: sessionId, duration_seconds: durationSeconds }, sanitizedPayload);

    if (this.isNativeApp && this.networkState.isOffline()) {
      return of({ success: true });
    }

    return this.http.post<any>(`${this.apiUrl}/progress/session/`, body);
  }

  // ===== USER PROGRESS =====

  getUserProgress(schein?: string): Observable<Progress> {
    if (this.isNativeApp && this.networkState.isOffline()) {
      return of({
        beantwortete_fragen: 0,
        richtige_antworten: 0,
        gesamt_fragen: 0,
        erfolgsquote: 0
      });
    }

    let params = new HttpParams();
    if (schein) params = params.set('schein', schein);
    return this.http.get<Progress>(`${this.apiUrl}/progress/user/`, { params });
  }

  getProgressByCategory(schein?: string): Observable<CategoryProgress[]> {
    if (this.isNativeApp && this.networkState.isOffline()) {
      return of([]);
    }

    let params = new HttpParams();
    if (schein) params = params.set('schein', schein);
    return this.http.get<CategoryProgress[]>(`${this.apiUrl}/progress/categories/`, { params });
  }

  getLearningTimeStats(period: 'day' | 'week' | 'month'): Observable<any> {
    if (this.isNativeApp && this.networkState.isOffline()) {
      return of({});
    }

    let params = new HttpParams().set('period', period);
    return this.http.get<any>(`${this.apiUrl}/progress/learning-time/`, { params });
  }

  // ===== USER PROFILE =====

  updateProfile(data: any): Observable<any> {
    if (this.isNativeApp && this.networkState.isOffline()) {
      return of({ success: false, message: 'Offline' });
    }
    return this.http.put<any>(`${this.apiUrl}/users/profile.php`, data);
  }

  deleteAccount(): Observable<any> {
    if (this.isNativeApp && this.networkState.isOffline()) {
      return of({ success: false, message: 'Offline' });
    }
    return this.http.delete<any>(`${this.apiUrl}/users/account.php`);
  }

  resetProgress(): Observable<any> {
    if (this.isNativeApp && this.networkState.isOffline()) {
      return of({ success: false, message: 'Offline' });
    }
    return this.http.post<any>(`${this.apiUrl}/users/progress-reset.php`, {});
  }

  // ===== EXAM MODE =====

  getExamBoegen(): Observable<any> {
    console.log(`[API] getExamBoegen aufgerufen - offline: ${this.networkState.isOffline()}`);

    // OFFLINE-FIRST STRATEGIE für Native App
    if (this.isNativeApp) {
      // 1. Versuche zuerst aus DB zu laden (AUCH WENN ONLINE!)
      return from(this.offlineDb.getExamBoegen()).pipe(
        switchMap(boegen => {
          if (boegen && boegen.length > 0) {
            // 2. Exam-Bögen in DB gefunden -> Verwende diese (spart Datenvolumen)
            console.log(`[API] 📱 Offline-First: Lade ${boegen.length} Exam-Bögen aus lokaler DB (spart Datenvolumen)`);
            const sample = boegen?.[0]
              ? {
                  id: boegen[0].id,
                  name: boegen[0].name,
                  schein: boegen[0].schein,
                  schein_kurz: boegen[0].schein_kurz,
                  schein_lang: (boegen[0] as any).schein_lang,
                  kurs_id: (boegen[0] as any).kurs_id
                }
              : null;
            console.log('[ExamDbg][API] source=sqlite count=', boegen?.length || 0, 'sample=', sample ? JSON.stringify(sample) : null);
            return of({ success: true, boegen: boegen });
          } else {
            // 3. Keine Exam-Bögen in DB -> Lade vom Server (nur wenn online)
            if (this.networkState.isOffline()) {
              console.warn('[API] ⚠️ Offline und keine Exam-Bögen im Cache');
              return of({ success: false, boegen: [] });
            }

            console.log('[API] 🌐 Lade Exam-Bögen vom Server (nicht im Cache)');
            return this.http.get<{ success: boolean; boegen: ExamBogen[] }>(`${this.apiUrl}/exams/boegen.php`).pipe(
              tap(response => {
                const sample = response?.boegen?.[0]
                  ? {
                      id: response.boegen[0].id,
                      name: response.boegen[0].name,
                      schein: (response.boegen[0] as any).schein,
                      schein_kurz: (response.boegen[0] as any).schein_kurz,
                      schein_lang: (response.boegen[0] as any).schein_lang,
                      kurs_id: (response.boegen[0] as any).kurs_id
                    }
                  : null;
                console.log('[ExamDbg][API] source=http success=', response?.success, 'count=', response?.boegen?.length || 0, 'sample=', sample ? JSON.stringify(sample) : null);
                if (response.success && response.boegen.length > 0) {
                  console.log(`[API] ${response.boegen.length} Exam-Bögen vom Server geladen`);
                  this.offlineDb.upsertExamBoegen(response.boegen).catch((err: any) => {
                    console.error('[API] Fehler beim Speichern von Exam-Bögen:', err);
                  });
                }
              }),
              catchError(error => {
                console.error('[API] Fehler beim Laden vom Server:', error);
                return of({ success: false, boegen: [] });
              })
            );
          }
        })
      );
    }

    // FALLBACK für Web-Browser
    if (this.networkState.isOffline()) {
      console.warn('[API] Browser offline - keine Exam-Bögen verfügbar');
      return of({ success: false, boegen: [] });
    }

    return this.http.get<{ success: boolean; boegen: ExamBogen[] }>(`${this.apiUrl}/exams/boegen.php`);
  }

  getExamBogen(bogenId: number): Observable<any> {
    console.log(`[API] getExamBogen aufgerufen - bogenId: ${bogenId}, offline: ${this.networkState.isOffline()}`);

    // OFFLINE-FIRST STRATEGIE für Native App
    if (this.isNativeApp) {
      // 1. Versuche zuerst aus DB zu laden (AUCH WENN ONLINE!)
      return from(this.offlineDb.getExamWithQuestions(bogenId)).pipe(
        switchMap(bogenData => {
          if (bogenData && bogenData.bogen && bogenData.fragen && bogenData.fragen.length > 0) {
            // 2. Exam-Bogen in DB gefunden -> Verwende diesen (spart Datenvolumen)
            console.log(`[API] 📱 Offline-First: Lade Exam-Bogen ${bogenId} mit ${bogenData.fragen.length} Fragen aus lokaler DB (spart Datenvolumen)`);
            return of(bogenData);
          } else {
            // 3. Exam-Bogen nicht in DB -> Lade vom Server (nur wenn online)
            if (this.networkState.isOffline()) {
              console.warn('[API] ⚠️ Offline und Exam-Bogen nicht im Cache');
              return of(null);
            }

            console.log(`[API] 🌐 Lade Exam-Bogen ${bogenId} vom Server (nicht im Cache)`);
            return this.http.get<ExamBogenDetails>(`${this.apiUrl}/exams/boegen.php?id=${bogenId}`).pipe(
              tap(response => {
                if (response && response.bogen && response.fragen) {
                  console.log(`[API] Exam-Bogen ${bogenId} mit ${response.fragen.length} Fragen vom Server geladen`);
                  const firstNonEmpty = (...vals: Array<string | null | undefined>) =>
                    vals.find(v => v !== undefined && v !== null && String(v).trim() !== '') || null;
                  const derivedKurz = firstNonEmpty(
                    (response.bogen as any).schein_kurz,
                    (response.bogen as any).schein,
                    (response.fragen?.[0] as any)?.schein
                  );
                  const derivedLang = firstNonEmpty(
                    (response.bogen as any).schein_lang,
                    (response.bogen as any).schein,
                    (response.fragen?.[0] as any)?.schein
                  );
                  // Speichere Bogen mit Fragen in DB
                  const bogenToSave = {
                    id: response.bogen.id,
                    name: response.bogen.name,
                    beschreibung: response.bogen.beschreibung,
                    schein: derivedLang || derivedKurz || response.bogen.schein,
                    schein_kurz: derivedKurz,
                    schein_lang: derivedLang,
                    kurs_id: response.bogen.kurs_id,
                    zeitlimit_minuten: response.bogen.zeitlimit_minuten,
                    bestehensgrenze_prozent: response.bogen.bestehensgrenze_prozent,
                    anzahl_fragen: response.anzahl_fragen,
                    is_offiziel: response.bogen.is_offiziel || 0,
                    fragen: response.fragen
                  };
                  this.offlineDb.upsertExamBoegen([bogenToSave]).catch((err: any) => {
                    console.error('[API] Fehler beim Speichern von Exam-Bogen:', err);
                  });
                }
              }),
              catchError(error => {
                console.error('[API] Fehler beim Laden vom Server:', error);
                // Fallback: Nochmal DB versuchen
                return from(this.offlineDb.getExamWithQuestions(bogenId));
              })
            );
          }
        })
      );
    }

    // FALLBACK für Web-Browser
    if (this.networkState.isOffline()) {
      console.warn('[API] Browser offline - Exam-Bogen nicht verfügbar');
      return of(null);
    }

    return this.http.get<ExamBogenDetails>(`${this.apiUrl}/exams/boegen.php?id=${bogenId}`);
  }

  saveExamResult(data: any): Observable<any> {
    // Offline: In DB speichern
    if (this.isNativeApp && this.networkState.isOffline()) {
      console.log('[API] Offline: Speichere Exam-Result in DB');
      return from(this.offlineDb.saveExamResult(data)).pipe(
        catchError(() => of({ success: false, ergebnis_id: 0, message: 'Offline - wird synchronisiert' }))
      );
    }

    return this.http.post<{ success: boolean; ergebnis_id: number; message: string }>(
      `${this.apiUrl}/exams/results.php`,
      data
    );
  }

  getExamResults(): Observable<any> {
    // Offline: Aus DB laden
    if (this.isNativeApp && this.networkState.isOffline()) {
      console.log('[API] Offline: Lade Exam-Results aus DB');
      return from(this.offlineDb.getExamResults()).pipe(
        catchError(() => of({ success: false, ergebnisse: [], statistik: {} }))
      );
    }

    return this.http.get<{ success: boolean; ergebnisse: any[]; statistik: any }>(
      `${this.apiUrl}/exams/results.php`
    );
  }

  getExamResultDetail(id: number): Observable<{ success: boolean; ergebnis: any }> {
    if (this.isNativeApp && this.networkState.isOffline()) {
      return of({ success: false, ergebnis: null });
    }

    return this.http.get<{ success: boolean; ergebnis: any }>(
      `${this.apiUrl}/exams/results.php?id=${id}`
    );
  }

  // ===== KURS-SCHULUNGSTAGE =====

  getMeineKurse(): Observable<any[]> {
    if (this.networkState.isOffline()) {
      return of([]);
    }
    return this.http
      .get<{ success: boolean; kurse: any[] }>(`${this.apiUrl}/kurse/meine.php`)
      .pipe(
        map(response => (Array.isArray(response?.kurse) ? response.kurse : [])),
        catchError(err => {
          console.error('[API] Fehler beim Laden der eigenen Kurse:', err);
          return of([]);
        })
      );
  }

  getKursSchulungstage(kursId: number): Observable<any[]> {
    if (this.isNativeApp && this.networkState.isOffline()) {
      console.warn('[API] Offline: Lade Schulungstage aus lokaler DB');
      return from(this.offlineDb.getKursSchulungstage(kursId));
    }

    const params = new HttpParams().set('kurs_id', kursId.toString());
    return this.http
      .get<{ success: boolean; entries: any[] }>(`${this.apiUrl}/kurse/schulungstage.php`, { params })
      .pipe(
        map(response => (Array.isArray(response?.entries) ? response.entries : [])),
        tap(entries => {
          if (this.isNativeApp && entries.length > 0) {
            this.offlineDb.upsertKursSchulungstage(kursId, entries).catch(err => {
              console.error('[API] Fehler beim Speichern der Schulungstage in DB', err);
            });
          }
        }),
        catchError(error => {
          console.error('[API] Fehler beim Laden der Schulungstage:', error);
          if (this.isNativeApp) {
            return from(this.offlineDb.getKursSchulungstage(kursId));
          }
          return of([]);
        })
      );
  }

  saveKursSchulungstage(kursId: number, entries: any[]): Observable<{ success: boolean; entries?: any[] }> {
    if (this.isNativeApp && this.networkState.isOffline()) {
      console.warn('[API] Offline: Schulungstage lokal gespeichert');
      this.offlineDb.upsertKursSchulungstage(kursId, entries).catch(err => {
        console.error('[API] Fehler beim lokalen Speichern der Schulungstage', err);
      });
      return of({ success: true, entries });
    }

    return this.http
      .put<{ success: boolean; entries: any[] }>(`${this.apiUrl}/kurse/schulungstage.php`, {
        kurs_id: kursId,
        entries
      })
      .pipe(
        tap(response => {
          if (this.isNativeApp && response?.entries) {
            this.offlineDb.upsertKursSchulungstage(kursId, response.entries).catch(err => {
              console.error('[API] Fehler beim Speichern der Schulungstage in DB', err);
            });
          }
        }),
        catchError(err => {
          console.error('[API] Fehler beim Speichern der Schulungstage:', err);
          return of({ success: false });
        })
      );
  }

  postSchulungstagAbsage(schulungstagId: number): Observable<{ success: boolean; created?: boolean; mail?: { attempted: number; sent: number; failed: number }; mail_debug?: any }> {
    if (this.networkState.isOffline()) {
      return of({ success: false });
    }
    const debugParam = this.shouldUseMailDebug() ? '?debug=1' : '';
    return this.http.post<{ success: boolean; created?: boolean; mail?: { attempted: number; sent: number; failed: number }; mail_debug?: any }>(
      `${this.apiUrl}/kurse/schulungstage-absage.php${debugParam}`,
      { schulungstag_id: schulungstagId }
    );
  }

  deleteSchulungstagAbsage(schulungstagId: number): Observable<{ success: boolean; deleted?: boolean }> {
    if (this.networkState.isOffline()) {
      return of({ success: false });
    }
    return this.http.post<{ success: boolean; deleted?: boolean }>(
      `${this.apiUrl}/kurse/schulungstage-absage-delete.php`,
      { schulungstag_id: schulungstagId }
    );
  }

  private shouldUseMailDebug(): boolean {
    const token = localStorage.getItem('token');
    if (!token) return false;
    const payload = this.decodeTokenPayload(token);
    return payload?.user_id === 22;
  }

  private decodeTokenPayload(token: string): any | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      return payload;
    } catch {
      return null;
    }
  }

  // ===== QUESTION PROGRESS =====

  getQuestionProgress(frageId?: number): Observable<QuestionProgress | { fortschritte: QuestionProgress[] }> {
    if (this.isNativeApp && this.networkState.isOffline()) {
      return of({ fortschritte: [] });
    }

    const url = frageId
      ? `${this.apiUrl}/progress/question-progress.php?frage_id=${frageId}`
      : `${this.apiUrl}/progress/question-progress.php`;
    return this.http.get<any>(url);
  }

  saveQuestionProgress(data: {
    frage_id: number;
    ist_richtig?: number;
    ist_gemerkt?: number
  }): Observable<{ success: boolean; message: string }> {
    if (this.isNativeApp && this.networkState.isOffline()) {
      return of({ success: true, message: 'Offline - wird synchronisiert' });
    }

    return this.http.post<{ success: boolean; message: string }>(
      `${this.apiUrl}/progress/question-progress.php`,
      data
    );
  }

  toggleBookmark(frageId: number, ist_gemerkt: number): Observable<{ success: boolean; message: string }> {
    return this.saveQuestionProgress({ frage_id: frageId, ist_gemerkt: ist_gemerkt });
  }

  // ===== EMAIL VERIFICATION =====

  verifyEmail(email: string, verificationCode: string): Observable<any> {
    if (this.isNativeApp && this.networkState.isOffline()) {
      return of({ success: false, message: 'Offline' });
    }

    return this.http.post<any>(`${this.apiUrl}/auth/verify-email.php`, {
      email: email,
      verification_code: verificationCode
    });
  }

  resendVerificationCode(email: string): Observable<any> {
    if (this.isNativeApp && this.networkState.isOffline()) {
      console.warn('[API] Offline: Verifizierungscode kann nicht erneut gesendet werden');
      return of({ success: false, message: 'Offline' });
    }

    return this.http.post<any>(`${this.apiUrl}/auth/resend-verification.php`, { email });
  }

  // ===== LEARNING STATISTICS =====

  getLearningStatistics(): Observable<any> {
    if (this.isNativeApp && this.networkState.isOffline()) {
      return of({});
    }

    return this.http.get<any>(`${this.apiUrl}/progress/learning-stats.php`);
  }

  getExtendedStatistics(schein?: string): Observable<any> {
    if (this.isNativeApp && this.networkState.isOffline()) {
      return of({
        scheins: [],
        gesamt: {
          beantwortete_fragen: 0,
          richtige_antworten: 0,
          falsche_antworten: 0,
          erfolgsquote: 0
        }
      });
    }

    let url = `${this.apiUrl}/progress/learning-stats-extended.php`;
    if (schein) {
      url += `?schein=${encodeURIComponent(schein)}`;
    }
    return this.http.get<ExtendedStatistics>(url);
  }

  getExamResultsBySchein(schein: string): Observable<{ success: boolean; ergebnisse: ExamResultsTableData[]; statistik: any }> {
    if (this.isNativeApp && this.networkState.isOffline()) {
      return of({ success: false, ergebnisse: [], statistik: {} });
    }

    const params = new HttpParams().set('schein', schein);
    return this.http.get<{ success: boolean; ergebnisse: ExamResultsTableData[]; statistik: any }>(
      `${this.apiUrl}/exams/results.php`,
      { params }
    );
  }

  logRegistrationError(payload: RegistrationErrorLogPayload): Observable<any> {
    if (this.networkState.isOffline()) {
      console.warn('[API] Offline: Registrierung-Fehlerlog nicht gesendet');
      return of(null);
    }

    return this.http.post<any>(`${this.apiUrl}/logs/register-error.php`, payload).pipe(
      catchError(err => {
        console.warn('[API] Registrierung-Fehlerlog konnte nicht gesendet werden', err);
        return of(null);
      })
    );
  }

  // ===== GENERIC METHODS =====

  get<T>(endpoint: string, params?: HttpParams): Observable<T> {
    return this.http.get<T>(`${this.apiUrl}/${endpoint}`, { params });
  }

  post<T>(endpoint: string, data: any): Observable<T> {
    if (this.isNativeApp && this.networkState.isOffline()) {
      console.warn('[API] Offline: POST request queued');
      return of(null as any);
    }
    return this.http.post<T>(`${this.apiUrl}/${endpoint}`, data);
  }

  put<T>(endpoint: string, data: any): Observable<T> {
    if (this.isNativeApp && this.networkState.isOffline()) {
      console.warn('[API] Offline: PUT request queued');
      return of(null as any);
    }
    return this.http.put<T>(`${this.apiUrl}/${endpoint}`, data);
  }

  delete<T>(endpoint: string): Observable<T> {
    if (this.isNativeApp && this.networkState.isOffline()) {
      console.warn('[API] Offline: DELETE request queued');
      return of(null as any);
    }
    return this.http.delete<T>(`${this.apiUrl}/${endpoint}`);
  }
}



